import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { FormGroup, FormBuilder, Validators } from '@angular/forms'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { BsModalRef } from 'ngx-bootstrap/modal'
import { combineLatest, Observable, of } from 'rxjs'
import {
  catchError,
  filter,
  map,
  startWith,
  take,
  timeout,
  withLatestFrom
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { Location } from 'src/app/features/location/location'
import { Dictionary } from '@ngrx/entity'
import { Product } from 'src/app/features/product/product'
import { productSelectors } from 'src/app/features/product/product.selectors'
import moment from 'moment-mini'
import { SerieProposal } from '../serie-proposal'
import {
  SerieProposalItem,
  SerieProposalItemStatus
} from '../serie-proposal-item/serie-proposal-item'
import { serieProposalItemActions } from '../serie-proposal-item/serie-proposal-item.actions'
import { serieProposalActions } from '../serie-proposal.actions'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { locationSelectors } from 'src/app/features/location/location.selectors'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { configurationOptionSelectors } from 'src/app/features/configuration-option/configuration-option.selectors'
import { productActions } from 'src/app/features/product/product.actions'
import { locationActions } from 'src/app/features/location/location.actions'
@UntilDestroy()
@Component({
  selector: 'serie-proposal-item-show-modal',
  templateUrl: './show-modal.component.html',
  styleUrls: ['./show-modal.component.scss']
})
export class SerieProposalItemShowModalComponent implements OnInit {
  @Input() serieProposal: SerieProposal
  @Input() serieProposalItem: SerieProposalItem
  @Output() updateSerieProposalItem: EventEmitter<any> = new EventEmitter()
  @Output() updateSerieProposal: EventEmitter<any> = new EventEmitter()
  public locations$: Observable<Location[]>
  public locationEntities$: Observable<Dictionary<Location>>
  public minDate = moment().toDate()
  public form: FormGroup
  public productEntities$: Observable<Dictionary<Product>>
  public selectedLanguage$: Observable<string>
  public serieProposalItemStatus = SerieProposalItemStatus
  public externalMemo: string = null
  public resources$: Observable<Product[]>
  private serieProposalProductGroupIds: number[] = []
  private locationIds: number[] = []
  constructor(
    private fb: FormBuilder,
    private notificationService: NotificationService,
    private store: Store<AppState>,
    private actions$: Actions,
    private bsModalRef: BsModalRef
  ) {}

  ngOnInit() {
    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )

    this.form = this.fb.group({
      startDate: [null, [Validators.required]],
      startTime: [null, [Validators.required]],
      endTime: [null, [Validators.required]],
      locationId: [null, [Validators.required]],
      productId: [null, [Validators.required]]
    })
    this.locationEntities$ = this.store.select(locationSelectors.selectEntities)
    this.productEntities$ = this.store.select(productSelectors.selectEntities)
    if (this.serieProposalItem.startDate) {
      this.form.controls['startDate'].patchValue(
        moment(this.serieProposalItem.startDate).toDate()
      )
      this.form.controls['startTime'].patchValue(
        moment(this.serieProposalItem.startDate).toDate()
      )
    }
    if (this.serieProposalItem.endDate) {
      this.form.controls['endTime'].patchValue(
        moment(this.serieProposalItem.endDate).clone().add(1, 'minute')
      )
    }
    this.store
      .select(configurationOptionSelectors.selectByKey, 'serie_proposal_config')
      .pipe(
        filter((configurationOption) => Boolean(configurationOption)),
        take(1),
        map((configurationOption) => JSON.parse(configurationOption?.value))
      )
      .subscribe((serieProposalConfigOptionValue) => {
        this.locationIds = serieProposalConfigOptionValue?.locationIds ?? []
        this.serieProposalProductGroupIds =
          serieProposalConfigOptionValue?.productGroupIds ?? []

        this.fetchLocations()
        this.fetchProducts()

        return
      })
    this.resources$ = combineLatest([
      this.store.select(productSelectors.selectEntities),
      this.form.controls.locationId.valueChanges.pipe(startWith(null))
    ]).pipe(
      untilDestroyed(this),
      map(([productEntities, locationIdValueChange]) =>
        Object.values(productEntities).filter(
          (product) =>
            product.bookable &&
            product.locationId == this.form.value.locationId &&
            product.productGroups?.some((productGroupId) =>
              this.serieProposalProductGroupIds.includes(productGroupId)
            )
        )
      )
    )
    this.locations$ = this.store.select(locationSelectors.selectAll).pipe(
      untilDestroyed(this),
      map((allLocations) => {
        if (!this.locationIds) {
          return []
        }
        return allLocations.filter((location) =>
          this.locationIds.includes(location.id)
        )
      })
    )
    this.form.controls.locationId.valueChanges
      .pipe(untilDestroyed(this), withLatestFrom(this.productEntities$))
      .subscribe(([locationId, productEntities]) => {
        if (
          locationId !=
          productEntities[this.serieProposal.productId]?.locationId
        ) {
          // fetch products of location
          this.fetchProducts(locationId)

          this.form.controls.productId.setValue(null)
          this.form.controls.productId.markAsDirty()
        } else {
          this.form.controls.productId.setValue(this.serieProposal.productId)
        }
      })
    if (this.serieProposalItem && this.serieProposalItem.productId) {
      this.productEntities$
        .pipe(take(1), withLatestFrom(this.locationEntities$))
        .subscribe(([productEntities, locationEntities]) => {
          this.form.controls.productId.patchValue(
            this.serieProposalItem.productId
          )
          this.form.controls.locationId.patchValue(
            productEntities[this.serieProposal.productId]?.locationId
          )
        })
    }
  }
  public onSearchProducts(term: string) {
    this.store.dispatch(
      productActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            description: { [CondOperator.CONTAINS_LOW]: term }
          }
        })
      })
    )
  }
  public onClickDeclineProposalItem() {
    this.store.dispatch(
      serieProposalItemActions.updateOneById({
        id: this.serieProposalItem.id,
        params: {
          status: SerieProposalItemStatus.DECLINED
        }
      })
    )
    this.handleUpsertManyAction()
  }
  private fetchLocations() {
    if (!this.locationIds?.length) {
      console.log(
        'No locationIds described in serie_proposal_config web option'
      )
      return
    }

    this.store.dispatch(
      locationActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: { id: { [CondOperator.IN]: this.locationIds } }
        })
      })
    )
  }

  private fetchProducts(locationId?: number) {
    if (!this.serieProposalProductGroupIds?.length) {
      console.log(
        'No productGroupIds described in serie_proposal_config web option'
      )
      return
    }

    this.store
      .select(productSelectors.selectEntities)
      .pipe(take(1))
      .subscribe((productEntities) => {
        const locationIdToFetch = locationId
          ? locationId
          : productEntities[this.serieProposal.productId]?.locationId

        this.store.dispatch(
          productActions.fetchMany({
            params: RequestQueryBuilder.create({
              search: {
                locationId: locationIdToFetch,
                bookable: true,
                isActive: true,
                'productGroups.id': {
                  [CondOperator.IN]: [...this.serieProposalProductGroupIds]
                }
              },
              join: [{ field: 'productGroups' }]
            })
          })
        )
      })
  }
  public onClickAcceptCounterProposal() {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsDirty()
      })
      console.log('%cForm is invalid', 'color: red; font-weight: bold;')
      return
    }

    this.store.dispatch(
      serieProposalItemActions.updateOneById({
        id: this.serieProposalItem.id,
        params: {
          status: SerieProposalItemStatus.ACCEPTED
        }
      })
    )
    this.handleUpsertManyAction()
  }

  public onFormSubmit(): void {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsDirty()
      })
      return
    }

    this.store.dispatch(
      serieProposalItemActions.updateOneById({
        id: this.serieProposalItem.id,
        params: {
          startDate: moment(this.form.value.startDate).set({
            hour: moment(this.form.value.startTime).get('hours'),
            minute: moment(this.form.value.startTime).get('minutes'),
            second: 0
          }),
          endDate: moment(this.form.value.startDate)
            .set({
              hour: moment(this.form.value.endTime).get('hours'),
              minute: moment(this.form.value.endTime).get('minutes'),
              second: 0
            })
            .subtract(1, 'minute'),
          status: SerieProposalItemStatus.COUNTER_PROPOSAL,
          productId: this.form.value.productId,
          dontSendMail: false
        }
      })
    )

    this.actions$
      .pipe(
        ofType(serieProposalItemActions.upsertMany),
        take(1),
        timeout(5000),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        if (this.serieProposal) {
          this.store.dispatch(
            serieProposalActions.fetchOneById({
              id: this.serieProposal.id,
              params: RequestQueryBuilder.create({
                join: [
                  { field: 'customer' },
                  { field: 'organization' },
                  { field: 'organization.tags' },
                  { field: 'serieProposalItems' },
                  { field: 'serieProposalItems.saleItem' }
                ]
              })
            })
          )
          this.store.dispatch(
            serieProposalItemActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: {
                  serieProposalId: {
                    [CondOperator.IN]: [this.serieProposal.id]
                  }
                },
                join: [
                  { field: 'serieProposal' },
                  { field: 'linkedProduct' },
                  { field: 'linkedProduct.translations' }
                ]
              })
            })
          )
        }
        if (action) {
          this.notificationService.emit('alerts.success', 'success')
          this.cancel()
        }
      })
  }

  public cancel() {
    this.bsModalRef.hide()
  }

  private handleUpsertManyAction() {
    this.actions$
      .pipe(
        ofType(serieProposalItemActions.upsertMany),
        take(1),
        timeout(5000),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        if (action) {
          // if (this.externalMemo != this.serieProposal.externalMemo) {
          //   this.store.dispatch(
          //     serieProposalActions.updateOneById({
          //       id: this.serieProposal.id,
          //       params: {
          //         externalMemo: this.externalMemo
          //       }
          //     })
          //   )
          // }
          this.updateSerieProposalItem.next(true)
          this.notificationService.emit('alerts.success', 'success')
          this.cancel()
        }
      })
  }
}
